import {
    Box,
    Divider,
    Flex,
    Heading,
    Image,
    Stack,
    Text,
    ThemeColorVariable,
    ThemeFontSizeVariable,
    ThemeFontVariable,
    ThemeSpaceVariable,
} from "@caspeco/casper-ui-library";
import { SafeHTML } from "components/safe-html";
import ActivityBadges from "components/selection-components/select-activity/activity-badges/activity-badges";
import { useLocalizedValue } from "helpers/use-localized-value";
import { ClickIdBuilders } from "logger/click-tracking";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { urlBuilder } from "search-params/url-builder";
import { Experience } from "types/experience";
import { ImageServiceAspectRatio } from "types/image-service-aspect-ratio";
import { AvailabilityState } from "utils/availability-utils";

interface ExperienceItemProps {
    experience: Experience;
    onClick: () => void;
    availabilityState?: AvailabilityState | null;
}

function ExperienceItem({ experience, onClick, availabilityState }: ExperienceItemProps) {
    const { t } = useTranslation();
    const [showImage, setShowImage] = useState(true);

    const experienceName = useLocalizedValue(experience.name);
    const experienceDescription = useLocalizedValue(experience.description);
    const { badgeSections, hasMenuChoice } = useMemo(
        () => ({
            badgeSections: experience.activities.map((activity) => ({
                timeSets: activity.timeSets,
            })),
            hasMenuChoice: experience.activities.some((activity) =>
                activity.timeSets.some((timeSet) => timeSet.menuGuid !== "")
            ),
        }),
        [experience.activities]
    );

    // Prefer 16x9 (large) image, fallback to 1x1 (small) image
    const imageReference = experience.imageReference16x9 || experience.imageReference1x1;
    const has16x9Image = showImage && !!experience.imageReference16x9;
    const has1x1Image =
        showImage && !!experience.imageReference1x1 && !experience.imageReference16x9;

    const imageParams: Record<string, string | undefined> = {
        AspectRatio: has16x9Image
            ? ImageServiceAspectRatio.SixteenByNine
            : ImageServiceAspectRatio.OneByOne,
        Type: "webp",
    };

    const baseUrl = imageReference
        ? `${import.meta.env.VITE_APP_IMAGE_SERVICE_URL}/${imageReference}`
        : "";
    const experienceImageUrl = imageReference ? urlBuilder(baseUrl, imageParams) : "";

    return (
        <Box
            as="button"
            w="full"
            onClick={onClick}
            type="button"
            aria-label={t("viewDetailsOfXActivity", { activity: experienceName })}
            data-track-click={ClickIdBuilders.selectActivity(experienceName)}
            // @ts-expect-error // ! TODO: Include _focus for box in casper-ui-library
            _focus={{ boxShadow: "var(--theme-shadows-outline)" }}
            _hover={{ bg: ThemeColorVariable.AccentHoverAlpha }}
        >
            <Flex
                justify="space-between"
                py={ThemeSpaceVariable.Medium}
                align="center"
                gap={ThemeSpaceVariable.Small}
                flex={1}
                px={{ md: ThemeSpaceVariable.Small }}
            >
                <Stack spacing={ThemeSpaceVariable.None} w="full">
                    {has16x9Image && (
                        <Image
                            src={experienceImageUrl}
                            borderRadius={ThemeSpaceVariable.Small}
                            onError={() => setShowImage(false)}
                            pb={ThemeSpaceVariable.Small}
                        />
                    )}
                    <Heading
                        as="h3"
                        noOfLines={1}
                        fontFamily={ThemeFontVariable.SubHeader}
                        fontWeight="medium"
                        color={ThemeColorVariable.OnBackgroundHeader}
                        fontSize={{
                            base: ThemeFontSizeVariable.Large,
                            md: ThemeFontSizeVariable.XLarge,
                        }}
                    >
                        {experienceName}
                    </Heading>
                    {experienceDescription && (
                        <Text
                            height={{
                                base: ThemeFontSizeVariable.XLarge,
                                md: "unset",
                            }}
                            noOfLines={1}
                            whiteSpace="break-spaces"
                            fontSize={{
                                base: ThemeFontSizeVariable.Small,
                                md: ThemeFontSizeVariable.Large,
                            }}
                            maxW="380px"
                        >
                            <SafeHTML
                                element="span"
                                options={{ allowedTags: ["br"], allowedSchemes: [] }}
                                html={experienceDescription}
                            />
                        </Text>
                    )}
                    <ActivityBadges
                        sections={badgeSections}
                        maximumGapTime={experience.maximumGapTime}
                        hasMenuChoice={hasMenuChoice}
                        availabilityState={availabilityState}
                    />
                </Stack>
                {has1x1Image && (
                    <Image
                        src={experienceImageUrl}
                        maxH="72px"
                        borderRadius={ThemeSpaceVariable.Small}
                        onError={() => setShowImage(false)}
                    />
                )}
            </Flex>
            <Divider />
        </Box>
    );
}

export default ExperienceItem;
